/**
 * What the Apply wrote, element by element.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Render rules copied from bank-move-to-live/dialog/RunResults: every skipped
 * element is NAMED, with its path and the tracker's own sentence quoted. A bare
 * count is exactly what this exists to prevent.
 *
 * The traffic shift is stated out loud. Dropping an archived lander from a path
 * hands its share to the survivors — inevitable (the tracker cannot serve an
 * archived lander) but a real routing change, so it is said, not buried.
 */

import { FC, ReactNode } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import type {
  FlowOptimizerApplyReport,
  FlowOptimizerApplyRow,
} from '@trafficpulse/shared';

import { applyHeadline } from './apply-report-summary';

const ICON = 'mt-0.5 h-3.5 w-3.5 shrink-0';

const Line: FC<{ icon: ReactNode; name: string; note: string }> = ({
  icon,
  name,
  note,
}) => (
  <li className="flex items-start gap-1.5">
    {icon}
    <span className="min-w-0">
      <span className="font-medium">{name}</span>
      <span className="text-muted-foreground"> — {note}</span>
    </span>
  </li>
);

const where = (row: FlowOptimizerApplyRow) =>
  row.pathName ? ` on path "${row.pathName}"` : '';

const skippedNote = (row: FlowOptimizerApplyRow) =>
  `was removed from the flow${where(row)} and keeps its old weight. The tracker said: "${row.message ?? 'no reason given'}"`;

export const ApplyReportList: FC<{ report: FlowOptimizerApplyReport }> = ({
  report,
}) => {
  const skipped = report.rows.filter((r) => !r.ok);
  return (
    <>
      <p>{applyHeadline(report)}</p>
      <ul className="mt-2 max-h-56 space-y-1 overflow-y-auto text-[12px]">
        {skipped.map((row) => (
          <Line
            key={`${row.pathId ?? ''}|${row.itemId}`}
            icon={<XCircle className={`${ICON} text-destructive`} aria-hidden />}
            name={row.itemName}
            note={skippedNote(row)}
          />
        ))}
        {report.rows
          .filter((r) => r.ok)
          .map((row) => (
            <Line
              key={`${row.pathId ?? ''}|${row.itemId}`}
              icon={
                <CheckCircle2 className={`${ICON} text-success`} aria-hidden />
              }
              name={row.itemName}
              note={`got its new weight${where(row)}`}
            />
          ))}
      </ul>
      {skipped.length > 0 && (
        <p className="mt-2 text-[12px] text-muted-foreground">
          The skipped elements are still archived in the tracker — restore or
          replace them there. Until then their share of the traffic goes to the
          other elements on the same path.
        </p>
      )}
    </>
  );
};

export default ApplyReportList;
